import React, { useState, useEffect } from 'react';
import { useUser } from '../../contexts/UserContext';
import { useSalon } from '../../contexts/SalonContext';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { format } from 'date-fns';

/**
 * Página de comissões do profissional
 */
const ProfessionalComissoesPage = () => {
  const { selectedSalonId } = useUser(); // Obter salonId do contexto do usuário
  const { currentUser } = useAuth();
  const { salonConfig } = useSalon();
  
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [professionalId, setProfessionalId] = useState(null);
  const [period, setPeriod] = useState('month'); // week, month, year, all
  const [stats, setStats] = useState({
    totalPending: 0,
    totalPaid: 0,
    count: 0
  });
  
  // Carregar dados do profissional e comissões
  useEffect(() => {
    const loadData = async () => {
      if (!selectedSalonId || !currentUser) {
        return;
      }
      
      setLoading(true);
      try {
        // 1. Buscar ID do profissional
        const profQuery = query(
          collection(db, 'salons', selectedSalonId, 'professionals'),
          where('userId', '==', currentUser.uid)
        );
        
        const profSnapshot = await getDocs(profQuery);
        if (profSnapshot.empty) {
          setError('Profissional não encontrado');
          setLoading(false);
          return;
        }
        
        const profId = profSnapshot.docs[0].id;
        setProfessionalId(profId);
        
        // 2. Buscar comissões
        let commissionsQuery;
        
        // Definir filtro de data com base no período selecionado
        const now = new Date();
        let startDate = new Date();
        
        if (period === 'week') {
          // Última semana
          startDate.setDate(now.getDate() - 7);
        } else if (period === 'month') {
          // Último mês
          startDate.setMonth(now.getMonth() - 1);
        } else if (period === 'year') {
          // Último ano
          startDate.setFullYear(now.getFullYear() - 1);
        } else if (period === 'all') {
          // Não aplicar filtro de data
          startDate = null;
        }
        
        if (startDate) {
          commissionsQuery = query(
            collection(db, 'salons', selectedSalonId, 'commissions'),
            where('professionalId', '==', profId),
            where('date', '>=', startDate),
            orderBy('date', 'desc')
          );
        } else {
          commissionsQuery = query(
            collection(db, 'salons', selectedSalonId, 'commissions'),
            where('professionalId', '==', profId),
            orderBy('date', 'desc')
          );
        }
        
        const commissionsSnapshot = await getDocs(commissionsQuery);
        const commissionsData = commissionsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: doc.data().date?.toDate()
        }));
        
        // Calcular estatísticas
        let totalPending = 0;
        let totalPaid = 0;
        
        commissionsData.forEach(commission => {
          if (commission.status === 'paid') {
            totalPaid += commission.amount || 0;
          } else {
            totalPending += commission.amount || 0;
          }
        });
        
        setCommissions(commissionsData);
        setStats({
          totalPending,
          totalPaid,
          count: commissionsData.length
        });
        
        setError(null);
      } catch (err) {
        console.error('Erro ao carregar comissões:', err);
        setError('Erro ao carregar comissões: ' + err.message);
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
  }, [selectedSalonId, currentUser, period]);
  
  const handlePeriodChange = (newPeriod) => {
    setPeriod(newPeriod);
  };
  
  // Renderizar estado de carregamento
  if (loading) {
    return (
      <div className="p-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }
  
  // Renderizar erro
  if (error) {
    return (
      <div className="p-4">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-500" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold text-gray-800 mb-4">Minhas Comissões</h1>
      
      {/* Cards de estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-gray-500 text-sm font-medium">Total Pendente</h3>
          <p className="text-2xl font-bold text-emerald-600">
            R$ {stats.totalPending.toFixed(2).replace('.', ',')}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-gray-500 text-sm font-medium">Total Pago</h3>
          <p className="text-2xl font-bold text-blue-600">
            R$ {stats.totalPaid.toFixed(2).replace('.', ',')}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-gray-500 text-sm font-medium">Quantidade</h3>
          <p className="text-2xl font-bold text-gray-800">
            {stats.count} comissões
          </p>
        </div>
      </div>
      
      {/* Filtros */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex space-x-4">
          <button
            className={`pb-3 px-1 ${period === 'week' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => handlePeriodChange('week')}
          >
            Semana
          </button>
          <button
            className={`pb-3 px-1 ${period === 'month' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => handlePeriodChange('month')}
          >
            Mês
          </button>
          <button
            className={`pb-3 px-1 ${period === 'year' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => handlePeriodChange('year')}
          >
            Ano
          </button>
          <button
            className={`pb-3 px-1 ${period === 'all' ? 'border-b-2 border-blue-500 text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => handlePeriodChange('all')}
          >
            Todas
          </button>
        </div>
      </div>
      
      {/* Tabela de comissões */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        {commissions.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-gray-500">Nenhuma comissão encontrada</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Serviço
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Valor
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {commissions.map(commission => (
                  <tr key={commission.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      {commission.date ? format(commission.date, 'dd/MM/yyyy') : 'Data não disponível'}
                    </td>
                    <td className="px-6 py-4">
                      {commission.serviceName || 'Serviço não especificado'}
                    </td>
                    <td className="px-6 py-4">
                      {commission.clientName || 'Cliente não especificado'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap font-medium">
                      R$ {(commission.amount || 0).toFixed(2).replace('.', ',')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span 
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                          ${commission.status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}
                      >
                        {commission.status === 'paid' ? 'Pago' : 'Pendente'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfessionalComissoesPage; 